
import * as React from "react"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"

interface InputProps extends React.ComponentProps<"input"> {
  onEnter?: () => void;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", inputMode, onEnter, ...props }, ref) => {
    const mobileInfo = useIsMobile();
    
    // Determine appropriate inputMode if not explicitly set
    const determinedInputMode = inputMode || (() => {
      switch (type) {
        case 'email': return 'email';
        case 'tel': 
        case 'phone': return 'tel';
        case 'number': return 'numeric';
        case 'decimal': return 'decimal';
        case 'search': return 'search';
        case 'url': return 'url';
        default: return undefined;
      }
    })();
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onEnter) {
        e.preventDefault();
        onEnter();
      }
      
      // Call the original onKeyDown if it exists
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };
    
    // Set focus with a slight delay on mobile to prevent keyboard issues
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (mobileInfo.isChromeOnAndroid) {
        // Add a small delay before focusing on Chrome Android
        // This helps avoid keyboard flickering issues
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
      
      // Call original onFocus if it exists
      if (props.onFocus) {
        props.onFocus(e);
      }
    };
    
    return (
      <input
        type={type === 'phone' ? 'tel' : type}
        inputMode={determinedInputMode}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          mobileInfo.isMobile ? "text-base py-3" : "", // Larger touch targets on mobile
          mobileInfo.isChromeOnAndroid ? "chrome-android-fix" : "", // Special class for Chrome on Android
          className
        )}
        ref={ref}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        autoCapitalize={type === 'email' ? 'off' : undefined}
        autoComplete={type === 'email' ? 'email' : type === 'tel' || type === 'phone' ? 'tel' : undefined}
        autoCorrect={type === 'email' || type === 'tel' || type === 'phone' ? 'off' : undefined}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
